import { useState, useEffect, useCallback } from 'react';
import type { ColumnPreferences, ColumnConfig } from './types';
import { getPreferencesKey } from './types';

interface UseColumnPreferencesOptions {
  siteId: string;
  contentType: string;
  columnConfig: ColumnConfig | null;
}

interface UseColumnPreferencesResult {
  // Current visible columns (from preferences or defaults)
  visibleColumns: string[];

  // All available columns from config
  allColumns: ColumnConfig['columns'];

  // Actions
  setVisibleColumns: (columns: string[]) => void;
  toggleColumn: (key: string) => void;
  resetToDefault: () => void;
  showAllColumns: () => void;

  // State
  isLoading: boolean;
  hasCustomPreferences: boolean;
}

/**
 * Hook for managing column visibility preferences with localStorage persistence.
 * Preferences are scoped per site + content type.
 *
 * Usage:
 * ```tsx
 * const { visibleColumns, toggleColumn, resetToDefault } = useColumnPreferences({
 *   siteId: '123',
 *   contentType: 'posts',
 *   columnConfig,
 * });
 * ```
 */
export function useColumnPreferences({
  siteId,
  contentType,
  columnConfig,
}: UseColumnPreferencesOptions): UseColumnPreferencesResult {
  const [preferences, setPreferences] = useState<ColumnPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const storageKey = getPreferencesKey(siteId, contentType);
  const defaultColumns = columnConfig?.defaultColumns || [];
  const allColumns = columnConfig?.columns || [];

  // Load preferences from localStorage on mount
  useEffect(() => {
    setIsLoading(true);

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnPreferences;
        // Validate that stored columns still exist in config
        if (columnConfig) {
          const validColumns = parsed.visibleColumns.filter(key =>
            columnConfig.columns.some(col => col.key === key)
          );
          if (validColumns.length > 0) {
            setPreferences({ ...parsed, visibleColumns: validColumns });
          } else {
            // All stored columns are invalid, reset to defaults
            setPreferences(null);
          }
        } else {
          setPreferences(parsed);
        }
      } else {
        setPreferences(null);
      }
    } catch (error) {
      console.warn('Failed to load column preferences:', error);
      setPreferences(null);
    }

    setIsLoading(false);
  }, [storageKey, columnConfig]);

  // Reset preferences when content type changes
  useEffect(() => {
    setPreferences(null);
    setIsLoading(true);

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && columnConfig) {
        const parsed = JSON.parse(stored) as ColumnPreferences;
        const validColumns = parsed.visibleColumns.filter(key =>
          columnConfig.columns.some(col => col.key === key)
        );
        if (validColumns.length > 0) {
          setPreferences({ ...parsed, visibleColumns: validColumns });
        }
      }
    } catch {
      // Ignore errors
    }

    setIsLoading(false);
  }, [contentType, siteId]);

  // Save preferences to localStorage
  const savePreferences = useCallback((prefs: ColumnPreferences) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(prefs));
    } catch (error) {
      console.warn('Failed to save column preferences:', error);
    }
  }, [storageKey]);

  // Get current visible columns
  const visibleColumns = preferences?.visibleColumns || defaultColumns;

  // Set visible columns
  const setVisibleColumns = useCallback((columns: string[]) => {
    const newPrefs: ColumnPreferences = {
      ...preferences,
      visibleColumns: columns,
    };
    setPreferences(newPrefs);
    savePreferences(newPrefs);
  }, [preferences, savePreferences]);

  // Toggle a single column
  const toggleColumn = useCallback((key: string) => {
    const current = preferences?.visibleColumns || defaultColumns;
    let newColumns: string[];

    if (current.includes(key)) {
      // Don't allow removing all columns
      if (current.length > 1) {
        newColumns = current.filter((k: string) => k !== key);
      } else {
        return; // Can't remove the last column
      }
    } else {
      // Add column - insert in the order it appears in config
      const configOrder = allColumns.map(c => c.key);
      newColumns = [...current, key].sort((a, b) => {
        return configOrder.indexOf(a) - configOrder.indexOf(b);
      });
    }

    setVisibleColumns(newColumns);
  }, [preferences, defaultColumns, allColumns, setVisibleColumns]);

  // Reset to default columns
  const resetToDefault = useCallback(() => {
    setPreferences(null);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore errors
    }
  }, [storageKey]);

  // Show all columns
  const showAllColumns = useCallback(() => {
    const allKeys = allColumns.map(c => c.key);
    setVisibleColumns(allKeys);
  }, [allColumns, setVisibleColumns]);

  return {
    visibleColumns,
    allColumns,
    setVisibleColumns,
    toggleColumn,
    resetToDefault,
    showAllColumns,
    isLoading,
    hasCustomPreferences: preferences !== null,
  };
}
